import React, { useState } from 'react';
import { Users, UserCheck, GraduationCap, DollarSign, CalendarCheck, BookOpen, AlertCircle, TrendingUp, Bus, Bell } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import StatCard from '../../components/common/StatCard';
import { formatCurrency, formatDate } from '../../utils/helpers';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

function AdminDashboard() {
  const { students, staff, classes, feePayments, feeStructures, attendance, exams, bookIssues, announcements, routes, transportTerms, academicYear } = useApp();
  const [notifStatus, setNotifStatus] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);

  const runNotifDiag = async () => {
    setNotifLoading(true); setNotifStatus(null);
    try {
      const token = JSON.parse(localStorage.getItem('sms_auth') || '{}')?.token || '';
      // 1. Check status
      const s = await fetch('/api/notifications/status', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
      // 2. Send test
      const t = await fetch('/api/notifications/send-test', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }).then(r => r.json());
      setNotifStatus({ ...s, test: t });
    } catch (e) { setNotifStatus({ error: e.message }); }
    finally { setNotifLoading(false); }
  };

  const activeStudents = students.filter(s=>s.status==='active').length;
  const activeStaff = staff.filter(s=>s.status==='active').length;

  // Tuition fee metrics (exclude transport payments from totals)
  const totalFeeCollected = feePayments.filter(p => p.type !== 'transportFee').reduce((sum,p)=>sum+Number(p.amount),0);
  const totalFeeDue = (() => {
    let due = 0;
    students.filter(s=>s.status==='active').forEach(s=>{
      const cls = classes.find(c=>c.id===s.classId);
      const className = cls?.name;
      const fs = [...feeStructures]
        .filter(f => (className && f.classId === className) || f.classId === s.classId)
        .filter(f => !academicYear || !f.academicYear || f.academicYear === academicYear)
        .sort((a, b) => (b.academicYear || '').localeCompare(a.academicYear || ''))[0] || null;
      if (!fs) return;
      const prevBal = s.previousYearBalance || 0;
      const paid = feePayments.filter(p=>p.studentId===s.id && p.type !== 'transportFee').reduce((sum,p)=>sum+Number(p.amount),0);
      due += Math.max(0, Number(fs.totalAmount||0) + Number(prevBal||0) - paid);
    });
    return due;
  })();

  // Transport metrics
  const transportStudents = students.filter(s => s.status === 'active' && s.transportRouteId);
  const transportStudentCount = transportStudents.length;

  const transportFeeCollected = feePayments.filter(p => p.type === 'transportFee').reduce((sum,p) => sum + Number(p.amount), 0);

  const transportFeeDue = (() => {
    let due = 0;
    transportStudents.forEach(s => {
      const route = routes.find(r => r.id === s.transportRouteId);
      const stop  = (route?.stops || []).find(st => st.name === s.transportStop);
      const annualFee = stop ? Number(stop.fee) : 0;
      if (annualFee <= 0) return;
      const paid = feePayments.filter(p => p.studentId === s.id && p.type === 'transportFee').reduce((sum,p) => sum + Number(p.amount), 0);
      due += Math.max(0, annualFee - paid);
    });
    return due;
  })();

  const today = new Date().toISOString().split('T')[0];
  const todayAttendance = attendance.filter(a=>a.date===today&&a.entityType==='student');
  const todayPresent = todayAttendance.filter(a=>a.status==='present').length;
  const overdueBooks = bookIssues.filter(i=>!i.returnDate && new Date(i.dueDate) < new Date()).length;

  const attendanceData = [
    { day: 'Mon', present: 8, absent: 2 },
    { day: 'Tue', present: 9, absent: 1 },
    { day: 'Wed', present: 7, absent: 3 },
    { day: 'Thu', present: 10, absent: 0 },
    { day: 'Fri', present: 8, absent: 2 },
  ];

  const feeData = [
    { name: 'Tuition Collected', value: totalFeeCollected, color: '#4f46e5' },
    { name: 'Tuition Due',       value: totalFeeDue,       color: '#f87171' },
    { name: 'Transport Collected', value: transportFeeCollected, color: '#0ea5e9' },
    { name: 'Transport Due',     value: transportFeeDue,   color: '#fb923c' },
  ];

  const classData = classes.map(c=>({
    name: c.name+'-'+c.section,
    students: students.filter(s=>s.classId===c.id&&s.status==='active').length
  }));

  // Transport route utilisation
  const routeUtilData = routes.map(r => ({
    name: r.routeName || r.name,
    enrolled: transportStudents.filter(s => s.transportRouteId === r.id).length,
  })).filter(r => r.enrolled > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-gray-500 text-sm mt-0.5">Academic Year {academicYear} &mdash; Welcome back! Here's your school overview.</p>
        </div>
        <button onClick={runNotifDiag} disabled={notifLoading}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50">
          <Bell size={14} /> {notifLoading ? 'Testing…' : 'Test Notifications'}
        </button>
      </div>

      {/* Notification diagnostic result */}
      {notifStatus && (
        <div className={`rounded-xl border p-4 text-sm ${notifStatus.error ? 'bg-red-50 border-red-200' : notifStatus.test?.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="font-semibold mb-2 flex items-center gap-2">
            <Bell size={14} />
            {notifStatus.error ? '❌ Diagnostic Error' : notifStatus.test?.success ? '✅ Notifications Working' : '⚠️ Issue Found'}
          </div>
          {notifStatus.error && <p className="text-red-700">{notifStatus.error}</p>}
          {!notifStatus.error && <>
            <p className={notifStatus.fcmReady ? 'text-green-700' : 'text-red-700'}>
              Firebase: {notifStatus.fcmReady ? '✅ Connected' : `❌ NOT initialized — ${notifStatus.fcmError || 'check firebase-credentials.json on server'}`}
            </p>
            <p className={notifStatus.tokenCount > 0 ? 'text-green-700' : 'text-red-700'}>
              Registered devices: {notifStatus.tokenCount} {notifStatus.tokenCount === 0 ? '← ❌ No device has logged in or registered token' : ''}
            </p>
            {notifStatus.test && <p className={notifStatus.test.success ? 'text-green-700' : 'text-red-700'}>
              Test send: {notifStatus.test.success ? `✅ Sent to ${notifStatus.test.sent}/${notifStatus.test.total} devices` : `❌ ${notifStatus.test.error}`}
            </p>}
            {notifStatus.test?.details?.map((d, i) => (
              <p key={i} className={d.success ? 'text-green-600' : 'text-red-600'} style={{fontSize:11}}>
                {d.success ? '✅' : '❌'} {d.name} ({d.role}){d.error ? ` — ${d.error}` : ''}
              </p>
            ))}
          </>}
        </div>
      )}

      {/* Row 1: Core stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={GraduationCap} label="Total Students" value={activeStudents} subtext={classes.length+' classes'} colorClass="bg-purple-500" />
        <StatCard icon={UserCheck} label="Total Staff" value={activeStaff} subtext="Active members" colorClass="bg-blue-500" />
        <StatCard icon={DollarSign} label="Fee Collected" value={formatCurrency(totalFeeCollected)} subtext={academicYear} colorClass="bg-green-500" />
        <StatCard icon={AlertCircle} label="Fee Due" value={formatCurrency(totalFeeDue)} subtext="Tuition pending" colorClass="bg-red-500" />
      </div>

      {/* Row 2: Secondary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label="Present Today" value={todayPresent+'/'+activeStudents} subtext="Student attendance" colorClass="bg-teal-500" />
        <StatCard icon={BookOpen} label="Overdue Books" value={overdueBooks} subtext="Library" colorClass="bg-orange-500" />
        <StatCard icon={TrendingUp} label="Upcoming Exams" value={exams.filter(e=>e.status==='scheduled').length} subtext="Scheduled" colorClass="bg-purple-500" />
        <StatCard icon={Users} label="Announcements" value={announcements.filter(a=>a.isActive).length} subtext="Active" colorClass="bg-pink-500" />
      </div>

      {/* Row 3: Transport stats */}
      {(transportStudentCount > 0 || routes.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Bus} label="On Transport" value={transportStudentCount} subtext={`${routes.length} route${routes.length !== 1 ? 's' : ''}`} colorClass="bg-sky-500" />
          <StatCard icon={DollarSign} label="Transport Collected" value={formatCurrency(transportFeeCollected)} subtext="This year" colorClass="bg-cyan-500" />
          <StatCard icon={AlertCircle} label="Transport Due" value={formatCurrency(transportFeeDue)} subtext="Pending collection" colorClass="bg-amber-500" />
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col justify-between">
            <p className="text-sm text-gray-500 mb-1">Transport Collection Rate</p>
            {transportFeeCollected + transportFeeDue > 0 ? (
              <>
                <p className="text-2xl font-bold text-sky-600">
                  {Math.round((transportFeeCollected / (transportFeeCollected + transportFeeDue)) * 100)}%
                </p>
                <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-sky-500 rounded-full transition-all"
                    style={{ width: `${Math.round((transportFeeCollected / (transportFeeCollected + transportFeeDue)) * 100)}%` }}
                  />
                </div>
              </>
            ) : (
              <p className="text-2xl font-bold text-gray-300">—</p>
            )}
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-4">Weekly Student Attendance</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={attendanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="present" name="Present" fill="#4f46e5" radius={[4,4,0,0]} />
              <Bar dataKey="absent" name="Absent" fill="#f87171" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-4">Fee Overview</h3>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={feeData.filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={35} outerRadius={60} dataKey="value">
                {feeData.filter(d => d.value > 0).map((entry,i)=><Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(val)=>formatCurrency(val)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-1">
            {feeData.filter(d => d.value > 0).map(d=>(
              <div key={d.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{backgroundColor:d.color}}></div>
                  <span className="text-gray-600">{d.name}</span>
                </div>
                <span className="font-medium">{formatCurrency(d.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-4">Students per Class</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={classData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
              <Tooltip />
              <Bar dataKey="students" name="Students" fill="#6366f1" radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {routeUtilData.length > 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Bus size={15} className="text-sky-500" /> Students per Route</h3>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={routeUtilData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
                <Tooltip />
                <Bar dataKey="enrolled" name="Students" fill="#0ea5e9" radius={[0,4,4,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-semibold mb-3">Recent Announcements</h3>
            <div className="space-y-2">
              {announcements.filter(a=>a.isActive).slice(0,4).map(ann=>(
                <div key={ann.id} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                  <div className={'w-2 h-2 rounded-full mt-1.5 flex-shrink-0 '+(ann.priority==='high'?'bg-red-500':ann.priority==='medium'?'bg-yellow-500':'bg-green-500')}></div>
                  <div>
                    <p className="text-sm font-medium">{ann.title}</p>
                    <p className="">{formatDate(ann.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
export default AdminDashboard;
